/**
 * Amplitude `Identify` helper for the appearance / theme cohort properties.
 *
 * Lives next to `amplitude-provider.tsx` (mirrors the canonical SDK call-site
 * pattern) so the namespace import is contained in one leaf module and the
 * F1 gate stays grep-discoverable (`// TELEMETRY-GATE: F1`).
 *
 * F1 scope: `isSaasTelemetryEnabled` is a build-time-folded `const boolean`,
 * so `if (!isSaasTelemetryEnabled) return;` lets DCE drop every Amplitude call
 * below — no `Identify`/`identify` ever executes on a suppressed build. This satisfies
 * the egress requirement. It does NOT guarantee full SDK removal from the
 * bundle: `@amplitude/analytics-browser` doesn't declare `sideEffects: false`,
 * so a few KB may remain in the dependency graph. Acceptable trade-off
 * (consistent with `amplitude-provider.tsx`); migrate to dynamic import if
 * bundle bloat ever matters.
 *
 * Called from `useAppearanceMode.setMode`, `UserThemeManager`'s boot
 * user-property effect, and `UserThemeManager`'s matchMedia listener. See
 * `docs/design-docs/dark-mode-flicker-fix.md` §7 for the full picture.
 */

import { type AppearanceMode, type ResolvedTheme } from '@/hooks/appearance-mode.types';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import * as amplitude from '@amplitude/analytics-browser';

/**
 * Last `(mode, resolved_theme)` tuple sent to Amplitude in this session.
 * Three sites write it — `useAppearanceMode.setMode`, `UserThemeManager`'s
 * boot user-property effect (once per `flagsReady` cycle, so again after each
 * `invalidatePosthogFlags` on logout), and its matchMedia listener — and they
 * routinely resolve to the same tuple. Without this short-circuit we'd send a
 * duplicate identify each time, costing $$ at MAU scale.
 *
 * Nothing resets it in production, so the first write of a page load is the one
 * that sticks. That is why `UserThemeManager` gates its user-property writes on
 * `flagsReady` — and only those, not the theme write — see its docblock before
 * making the cache resettable.
 *
 * Exported for tests to reset between cases.
 */
let lastSentUserProperties: {
  mode: AppearanceMode;
  resolvedTheme: ResolvedTheme;
} | null = null;

export function resetAppearanceUserPropertiesCache(): void {
  lastSentUserProperties = null;
}

/**
 * Set Amplitude user properties so every other event in the codebase can be
 * sliced by current theme without those events having to change.
 */
export function setAppearanceUserProperties(
  mode: AppearanceMode,
  resolvedTheme: ResolvedTheme
): void {
  // TELEMETRY-GATE: F1 function-entry gate. Mirrors the canonical pattern in
  // src/providers/amplitude-provider.tsx — Amplitude must not be driven via
  // .identify() when telemetry is suppressed. Bundlers fold the literal-false
  // branch and tree-shake the @amplitude/analytics-browser usage out of the
  // suppressed bundle so a leaked key cannot re-enable egress. See
  // .ai-context/onprem-guardrails.md (rule F1).
  if (!isSaasTelemetryEnabled) return;

  if (
    lastSentUserProperties?.mode === mode &&
    lastSentUserProperties.resolvedTheme === resolvedTheme
  ) {
    return;
  }
  lastSentUserProperties = { mode, resolvedTheme };

  try {
    const identify = new amplitude.Identify();
    identify.set('appearance_mode', mode);
    identify.set('resolved_theme', resolvedTheme);
    amplitude.identify(identify);
  } catch {
    // Amplitude unavailable (SSR, blocked by adblock, etc.) — non-fatal.
  }
}
